/**
 * EmbedBlock — real block for <YouTube id="" title="" /> and <Loom id="" title="" />.
 * Inserted from the slash menu with an empty id; the block shows a URL/ID box.
 */
import { useState } from 'react';
import { Node, mergeAttributes } from '@tiptap/core';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import { Trash2, Youtube, Video } from 'lucide-react';
import { useEditorTheme } from '../EditorThemeContext';
import { mdxAttr } from '../mdxAttr';

export const extractEmbedId = (kind, value) => {
  const v = (value || '').trim();
  if (!v) return '';
  if (kind === 'Loom') {
    const m = v.match(/loom\.com\/(?:share|embed)\/([A-Za-z0-9]+)/);
    return m ? m[1] : v.replace(/[^A-Za-z0-9]/g, '');
  }
  const m = v.match(/(?:v=|youtu\.be\/|embed\/|shorts\/)([\w-]{11})/);
  return m ? m[1] : v;
};

const embedSrc = (kind, id) =>
  kind === 'Loom' ? `https://www.loom.com/embed/${id}` : `https://www.youtube.com/embed/${id}`;

const EmbedView = ({ node, updateAttributes, deleteNode }) => {
  const isLight = useEditorTheme() === 'light';
  const { kind, id, title } = node.attrs;
  const [input, setInput] = useState('');
  const Icon = kind === 'Loom' ? Video : Youtube;
  const box = isLight ? 'border-gray-200 bg-gray-50' : 'border-white/10 bg-[#1a1a1a]';
  const field = isLight ? 'bg-white border-gray-300 text-gray-900' : 'bg-slate-800 border-slate-700 text-white';
  const commit = () => {
    const nid = extractEmbedId(kind, input);
    if (nid) updateAttributes({ id: nid });
  };
  return (
    <NodeViewWrapper className="embed-block-wrapper my-4 group/embed" data-testid={`embed-block-${(kind || '').toLowerCase()}`} contentEditable={false}>
      <div className={`relative rounded-xl border overflow-hidden ${box}`}>
        {id ? (
          <iframe
            src={embedSrc(kind, id)}
            title={title || `${kind} video`}
            className="w-full aspect-video"
            frameBorder="0"
            allowFullScreen
            data-testid="embed-player"
          />
        ) : (
          <div className="p-6 flex flex-col gap-3">
            <div className={`flex items-center gap-2 text-sm font-medium ${isLight ? 'text-gray-700' : 'text-slate-200'}`}>
              <Icon className="w-4 h-4 text-[#00A1B2]" /> {kind} embed
            </div>
            <div className="flex gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    commit();
                  }
                }}
                placeholder={kind === 'Loom' ? 'Paste a Loom share URL or ID' : 'Paste a YouTube URL or video ID'}
                className={`flex-1 px-3 py-2 rounded-lg border text-sm focus:outline-none focus:border-[#00A1B2] ${field}`}
                data-testid="embed-url-input"
              />
              <button type="button" onClick={commit} className="px-3 py-2 rounded-lg bg-[#00A1B2] text-white text-sm" data-testid="embed-url-set">
                Embed
              </button>
            </div>
          </div>
        )}
        <div className="absolute top-2 right-2 flex items-center gap-1 opacity-0 group-hover/embed:opacity-100 transition-opacity">
          <input
            value={title || ''}
            onChange={(e) => updateAttributes({ title: e.target.value })}
            placeholder="Title"
            className={`w-40 px-2 py-1 rounded-lg border text-xs focus:outline-none ${field}`}
            data-testid="embed-title-input"
          />
          <button
            type="button"
            onClick={() => deleteNode()}
            className="p-1.5 rounded-lg text-white bg-black/60 hover:bg-black/80"
            title="Delete embed"
            data-testid="embed-delete"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </NodeViewWrapper>
  );
};

export const EmbedBlock = Node.create({
  name: 'embedBlock',
  group: 'block',
  atom: true,
  draggable: true,

  addAttributes() {
    return {
      kind: { default: 'YouTube', parseHTML: (el) => el.getAttribute('data-kind') || 'YouTube', rendered: false },
      id: { default: '', parseHTML: (el) => el.getAttribute('data-id') || '', rendered: false },
      title: { default: '', parseHTML: (el) => el.getAttribute('data-title') || '', rendered: false },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-type="embed-block"]' }];
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-type': 'embed-block',
        'data-kind': node.attrs.kind,
        'data-id': node.attrs.id,
        'data-title': node.attrs.title,
      }),
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(EmbedView, {
      stopEvent: ({ event }) => event.target?.tagName === 'INPUT' || event.type === 'mousedown' || event.type === 'click',
    });
  },

  addStorage() {
    return {
      markdown: {
        serialize(state, node) {
          const { kind, id, title } = node.attrs;
          if (id) {
            const t = title ? ' ' + mdxAttr('title', title) : '';
            state.write(`<${kind || 'YouTube'} ${mdxAttr('id', id)}${t} />\n`);
          }
          state.closeBlock(node);
        },
      },
    };
  },
});
